import { __awaiter, __generator, __spreadArray } from "tslib";
import * as signalR from '@microsoft/signalr';
import HubReceiver from "./HubReceiver";
import { AppServices, Misc, TaskRunner } from '../';
import { ConnectionState } from '../ConnectionManager';
import { ObservableValue } from '../Model';
import { Utils } from '../Utils';
var HubBase = /** @class */ (function () {
    /**
     * Sets up the signalR hub.
     * @param url Hub endpoint.
     * @param options Hub options.
     * @param authenticationService Optional auth service if you don't want it to get from DI.
     */
    function HubBase(url, options, configModel, authenticationService) {
        if (configModel === void 0) { configModel = Misc.Globals.appService.get(AppServices.NeoTypes.Config.ConfigModel); }
        if (authenticationService === void 0) { authenticationService = Misc.Globals.appService.get(AppServices.NeoTypes.Security.AuthenticationService); }
        var _this = this;
        this.url = url;
        this.authenticationService = authenticationService;
        this.receivers = new Map();
        this._connectionState = new ObservableValue(ConnectionState.NotStarted);
        this.options = Utils.populatePartialWithDefaults(options, { reconnectOnDisconnect: true, retryCount: 3, retryInterval: 30000, developmentLogLevel: signalR.LogLevel.Debug, releaseLogLevel: signalR.LogLevel.Error });
        var builder = new signalR.HubConnectionBuilder()
            .withUrl(this.url, {
            accessTokenFactory: function () { return _this.authenticationService.user ? _this.authenticationService.user.accessToken : ""; }
        })
            .configureLogging(configModel.isDevelopment ? this.options.developmentLogLevel : this.options.releaseLogLevel);
        if (this.options.reconnectOnDisconnect) {
            var retries = [];
            for (var i = 0; i < this.options.retryCount; i++) {
                retries.push(this.options.retryInterval);
            }
            builder = builder.withAutomaticReconnect(retries);
        }
        this.connection = builder.build();
        // These allow consumers of this service to subscribe to connection events.
        this.onDisconnected = new HubReceiver(function (push) {
            _this.disconnectedPush = push;
        });
        this.onConnected = new HubReceiver(function (push) {
            _this.connectPush = push;
        });
        // These are used internally
        this.connection.onreconnected(function () {
            _this._connectionState.value = ConnectionState.Connected;
            _this.connectPush(false);
        });
        this.connection.onreconnecting(function () {
            _this._connectionState.value = ConnectionState.ReConnecting;
        });
        this.connection.onclose(function (error) {
            _this._connectionState.value = ConnectionState.Disconnected;
            _this.disconnectedPush(error);
        });
    }
    Object.defineProperty(HubBase.prototype, "connectionState", {
        get: function () {
            return this._connectionState.value;
        },
        enumerable: false,
        configurable: true
    });
    HubBase.prototype.connect = function () {
        return __awaiter(this, void 0, void 0, function () {
            var i, e_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.connection.state === signalR.HubConnectionState.Disconnected)) return [3 /*break*/, 8];
                        i = 0;
                        _a.label = 1;
                    case 1:
                        if (!(i < this.options.retryCount)) return [3 /*break*/, 8];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, this.connection.start()];
                    case 3:
                        _a.sent();
                        this._connectionState.value = ConnectionState.Connected;
                        this.connectPush(true);
                        return [2 /*return*/, true];
                    case 4:
                        e_1 = _a.sent();
                        if (i >= this.options.retryCount) {
                            this._connectionState.value = ConnectionState.Disconnected;
                            throw e_1;
                        }
                        else {
                            this._connectionState.value = ConnectionState.ReConnecting;
                        }
                        return [3 /*break*/, 5];
                    case 5: return [4 /*yield*/, TaskRunner.delay(this.options.retryInterval)];
                    case 6:
                        _a.sent();
                        _a.label = 7;
                    case 7:
                        i++;
                        return [3 /*break*/, 1];
                    case 8: return [2 /*return*/, false];
                }
            });
        });
    };
    HubBase.prototype.connectIfAuthenticated = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this.authenticationService.isAuthenticated) {
                    return [2 /*return*/, this.connect()];
                }
                else {
                    return [2 /*return*/, Promise.resolve(false)];
                }
                return [2 /*return*/];
            });
        });
    };
    HubBase.prototype.disconnect = function () {
        return this.connection.stop();
    };
    /** Disconnects the hub client from the hub. Subscriptions are not disposed. */
    HubBase.prototype.dispose = function () {
        this.disconnect();
    };
    /**
     * Invokes a method on the server hub, returning a promise.
     * The Promise is resolved with the return value (if any) when the method on the server returns.
     * If the method on the server throws an error, the Promise is rejected with the error message.
     */
    HubBase.prototype.invoke = function (methodName) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return (_a = this.connection).invoke.apply(_a, __spreadArray([methodName], args, false));
    };
    /**
     * Invokes a method on the server hub, returning a promise.
     * Note: Using send doesn't wait until the server has received the message.
     * Consequently, it's not possible to return data or errors from the server.
     * Use with caution.
     */
    HubBase.prototype.send = function (methodName) {
        var _a;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        return (_a = this.connection).send.apply(_a, __spreadArray([methodName], args, false));
    };
    HubBase.prototype.registerReceiver = function (methodName) {
        var _this = this;
        var receiver = this.receivers.get(methodName);
        if (!receiver) {
            receiver = new HubReceiver(function (push) {
                _this.connection.on(methodName, function (value) {
                    push(value);
                });
            });
            this.receivers.set(methodName, receiver);
        }
        return receiver;
    };
    return HubBase;
}());
export { HubBase };
